import api from '../../api'
import { ConfirmUploadRequest, ConfirmUploadResponse, GenerateUploadUrlResponse } from '../../../functions/chunkedUpload'
import {
  confirmUploadUrl,
  directUpload,
  discardOrphanedUpload,
  generateUploadUrl,
  uploadFileForContent,
} from '../../uploads'

export { discardOrphanedUpload }

export const getPosts = async (data: {
  page: number
  page_size: number
  member_id?: string
  following?: boolean
  liked?: boolean
}) => {
  const res = await api.get('post/', {
    params: data,
  })
  return res.data
}

export const getPostDetail = async (data: { id: string }) => {
  const res = await api.get('post/' + data.id)
  return res.data
}

export const getPostComments = async ({
  id,
  data,
}: {
  id: string
  data: {
    page: number
    page_size: number
  }
}) => {
  const res = await api.get(`post/${id}/comments/`, { params: data })
  return res.data
}

export const postReport = async ({
  id,
  data,
}: {
  id: string
  data: {
    category: string
  }
}) => {
  const res = await api.post(`post/${id}/report/`, data)
  return res.data
}

export const postComment = async ({
  id,
  data,
}: {
  id: string
  data: {
    comment: string
    mentions: string[]
    venue_mentions?: string[]
  }
}) => {
  const res = await api.post(`post/${id}/comments/`, {
    ...data,
    mentions: data.mentions.join(','),
    venue_mentions: (data.venue_mentions ?? []).join(','),
  })
  return res.data
}

export const putLike = async ({
  id,
  data,
}: {
  id: string
  data: {
    like: boolean
  }
}) => {
  const res = await api.put(`post/${id}/likes/`, data)
  return res.data
}

export const postBlock = async ({ id }: { id: string }) => {
  const res = await api.post(`member/${id}/block/`)
  return res.data
}

export const postGenerateUploadUrl = async (data: {
  filename: string
  filetype: string
  chunk_count: number
  content_type?: string
}): Promise<GenerateUploadUrlResponse> => generateUploadUrl(data as Parameters<typeof generateUploadUrl>[0])

export const postConfirmUploadUrl = async (
  data: ConfirmUploadRequest
): Promise<ConfirmUploadResponse> => confirmUploadUrl(data)

export const postDirectUpload = async (data: {
  file: File
  content_type: string
  onProgress?: (progress: number) => void
}): Promise<string> => directUpload(data as Parameters<typeof directUpload>[0])

/**
 * Upload a file for a post. Returns the uploaded_file_id.
 * Used both for early upload (on cropper Save) and for upload-on-Share.
 */
export const uploadFileForPost = async (params: {
  file: File
  filePath?: string
  onProgress?: (progress: number) => void
  abortSignal?: AbortSignal
  /** Called when presigned URLs are received (chunked flow), so caller can discard on cancel */
  onGenerateResult?: (result: GenerateUploadUrlResponse) => void
}): Promise<string> => {
  const contentType = params.file.type.startsWith('video/') ? 'video' : 'photo'
  return uploadFileForContent({ ...params, contentType })
}

/**
 * Create a post with platform-aware file upload
 * - If uploaded_file_id is provided, skips upload and creates the post with that file
 * - Otherwise uploads the file first (Mobile: capacitor-uploader, Web: chunked/direct)
 */
export const postPost = async (data: {
  file: File | null
  caption: string
  location: string
  mentions: string[]
  venue_mentions?: string[]
  onProgress?: (progress: number) => void
  abortSignal?: AbortSignal
  filePath?: string
  /** When set, upload is skipped and this id is used for the post (early-upload flow) */
  uploaded_file_id?: string
}) => {
  let uploadedFileId: string

  if (data.uploaded_file_id) {
    uploadedFileId = data.uploaded_file_id
  } else {
    if (!data.file) {
      const error: any = new Error('No file provided for upload')
      error.response = {
        data: {
          success: false,
          message: "Post creation failed: {'file': ['File is required']}",
          status_code: 400,
        },
      }
      throw error
    }
    if (!(data.file instanceof File)) {
      const error: any = new Error('Invalid file object')
      error.response = {
        data: {
          success: false,
          message: "Post creation failed: {'file': ['File is required']}",
          status_code: 400,
        },
      }
      throw error
    }
    if (!data.file.name || data.file.name.trim() === '') {
      const error: any = new Error('File name is missing')
      error.response = {
        data: {
          success: false,
          message: "Post creation failed: {'file': ['Invalid file: file name is missing']}",
          status_code: 400,
        },
      }
      throw error
    }
    if (!data.file.name.includes('.')) {
      const error: any = new Error('File must have an extension')
      error.response = {
        data: {
          success: false,
          message: "Post creation failed: {'file': ['Invalid file: file must have an extension']}",
          status_code: 400,
        },
      }
      throw error
    }
    uploadedFileId = await uploadFileForPost({
      file: data.file,
      filePath: data.filePath,
      onProgress: data.onProgress,
      abortSignal: data.abortSignal,
    })
  }

  try {
    const res = await api.post(
      'post/myposts/',
      {
        uploaded_file_id: uploadedFileId, // Primary parameter name
        file_id: uploadedFileId, // Also send for backward compatibility
        caption: data.caption,
        location: data.location,
        mentions: data.mentions.join(','),
        venue_mentions: (data.venue_mentions ?? []).join(','),
      },
      {
        timeout: 30000, // 30 seconds for post creation
      }
    )

    return res.data
  } catch (error: any) {
    // Re-throw with proper error format if it's not already formatted
    if (!error.response) {
      const formattedError: any = new Error(error.message || 'Upload failed')
      formattedError.response = {
        data: {
          success: false,
          message: error.message || 'Post creation failed',
          status_code: 500,
        },
      }
      throw formattedError
    }
    throw error
  }
}

export const putPost = async ({
  id,
  data,
}: {
  id: string
  data: {
    caption: string
    location: string
    mentions: string[]
    venue_mentions?: string[]
  }
}) => {
  const res = await api.patch(`post/${id}/`, {
    ...data,
    mentions: data.mentions.join(','),
    venue_mentions: (data.venue_mentions ?? []).join(','),
  })
  return res.data
}

export const deletePost = async (data: { id: string }) => {
  const res = await api.delete('post/myposts/', { data })
  return res.data
}

export const getSuggestedMembers = async (data: { page: number; page_size: number; exclude_ids?: string }) => {
  const res = await api.get('member/', { params: data })
  return res.data
}

export const getMembers = async (data: {
  search?: string
  page_size?: number
  page?: number
  exclude_ids?: string
  gender?: string
  age_range?: string
  skill_level?: string
  offers_private_lessons?: boolean | null
  fields?: string[]
}) => {
  const res = await api.get('member/', { params: { ...data, fields: data.fields?.join(',') } })
  return res.data
}

export const getVenuesMinimal = async (data: { venue_search: string; page_size?: number }) => {
  const res = await api.get('venues/', {
    params: { minimal: true, venue_search: data.venue_search, page_size: data.page_size ?? 10 },
  })
  return res.data
}
